import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Injectable, inject } from '@angular/core';
import { marker as T } from '@biesbjerg/ngx-translate-extract-marker';
import { TnDialog } from '@truenas/ui-components';
import {
  defer, EMPTY, finalize, MonoTypeOperatorFunction, Observable, Subscription,
} from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AppLoaderComponent } from 'app/modules/loader/components/app-loader/app-loader.component';
import { FocusService } from 'app/services/focus.service';

@Injectable({ providedIn: 'root' })
export class LoaderService {
  private tnDialog = inject(TnDialog);
  // The cdk `Dialog` is the underlying service `TnDialog` wraps; we inject it
  // directly to read `openDialogs` for focus restoration, which `TnDialog` does
  // not expose. Mirrors the pattern in DialogService.
  private dialog = inject(Dialog);
  private focusService = inject(FocusService);

  dialogRef: DialogRef<unknown, AppLoaderComponent> | null = null;
  private onBeforeClose: (() => Observable<boolean>) | null = null;
  private handlersSetup = false;
  private keydownSubscription: Subscription | null = null;
  private backdropSubscription: Subscription | null = null;

  /**
   * Opens loader when observable (request) starts and closes when it ends.
   */
  withLoader<T>(): MonoTypeOperatorFunction<T> {
    return (source$: Observable<T>) => defer(() => {
      this.open();
      return source$.pipe(
        finalize(() => this.close()),
      );
    });
  }

  open(title: string = T('Please wait')): Observable<void> {
    if (this.dialogRef !== null) {
      return EMPTY;
    }

    this.dialogRef = this.tnDialog.open<AppLoaderComponent>(AppLoaderComponent, {
      // The loader must never be dismissed by the user (backdrop click / ESC);
      // it only closes programmatically when loading finishes. The optional
      // confirmation flow listens to keydownEvents/backdropClick manually,
      // which still emit while disableClose is true.
      disableClose: true,
      // `TnDialog` prepends its `tn-dialog-panel` class (which supplies the
      // surface — background, border-radius, box-shadow); `app-loader-overlay`
      // remains for the loader-specific overlay tweaks in _tn-styles.scss.
      panelClass: 'app-loader-overlay',
      width: '200px',
      height: '200px',
    });

    if (this.dialogRef.componentInstance) {
      this.dialogRef.componentInstance.setTitle(title);
    }

    // If there's a confirmation handler, intercept close attempts
    if (this.onBeforeClose) {
      this.setupConfirmationHandlers();
    }

    return this.dialogRef.closed as Observable<void>;
  }

  close(): void {
    if (this.dialogRef) {
      this.tryToRestoreFocusToThePreviousDialog();
      this.dialogRef.close();
      this.dialogRef = null;
      this.onBeforeClose = null;
      this.handlersSetup = false;

      // Clean up subscriptions
      if (this.keydownSubscription) {
        this.keydownSubscription.unsubscribe();
        this.keydownSubscription = null;
      }
      if (this.backdropSubscription) {
        this.backdropSubscription.unsubscribe();
        this.backdropSubscription = null;
      }
    }
  }

  /**
   * Sets a confirmation handler that will be called when the user tries to close the loader
   * (by clicking outside or pressing ESC). If the handler returns true, the loader will close.
   * If false, it stays open.
   */
  setConfirmationBeforeClose(handler: () => Observable<boolean>): void {
    this.onBeforeClose = handler;
    // If dialog is already open, update its disableClose property and setup handlers
    if (this.dialogRef) {
      this.dialogRef.disableClose = true;
      this.setupConfirmationHandlers();
    }
  }

  /**
   * Removes the confirmation handler
   */
  removeConfirmationBeforeClose(): void {
    this.onBeforeClose = null;
    this.handlersSetup = false;

    // Unsubscribe from event handlers
    if (this.keydownSubscription) {
      this.keydownSubscription.unsubscribe();
      this.keydownSubscription = null;
    }
    if (this.backdropSubscription) {
      this.backdropSubscription.unsubscribe();
      this.backdropSubscription = null;
    }

    // The loader still must not be user-dismissible once the confirmation
    // handler is removed; it only closes programmatically.
    if (this.dialogRef) {
      this.dialogRef.disableClose = true;
    }
  }

  private setupConfirmationHandlers(): void {
    if (!this.dialogRef || !this.onBeforeClose || this.handlersSetup) {
      return;
    }

    this.handlersSetup = true;

    // Since disableClose is true, we need to manually listen for ESC and backdrop clicks.
    // On cdk DialogRef these are observable properties (not methods like on MatDialogRef).
    this.keydownSubscription = this.dialogRef.keydownEvents.pipe(
      filter((event) => event.key === 'Escape'),
    ).subscribe(() => {
      this.handleConfirmationClose();
    });

    this.backdropSubscription = this.dialogRef.backdropClick.subscribe(() => {
      this.handleConfirmationClose();
    });
  }

  private handleConfirmationClose(): void {
    // Check if handler is still active (not removed)
    if (this.onBeforeClose && this.handlersSetup) {
      // take(1): the confirmation is one-shot, so a caller returning a
      // long-lived or multi-emitting observable can't leak or fire close() twice.
      this.onBeforeClose().pipe(take(1)).subscribe((shouldClose) => {
        if (shouldClose) {
          this.close();
        }
        // If shouldClose is false, do nothing (dialog stays open)
      });
    }
  }

  setTitle(title: string): void {
    if (!this.dialogRef?.componentInstance) {
      return;
    }

    this.dialogRef.componentInstance.setTitle(title);
  }

  private tryToRestoreFocusToThePreviousDialog(): void {
    const previousDialogs = this.dialog.openDialogs.filter((dialog) => dialog.id !== this.dialogRef?.id);
    const previousDialogId = previousDialogs[previousDialogs.length - 1]?.id;

    if (previousDialogId) {
      this.focusService.focusElementById(previousDialogId);
    }
  }
}
